import { Button } from '../../components/Button';
import { Toast, toastTitleVariants } from '../../components/Toast';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { MemberAvatarStack } from '../MemberAvatarStack';
import type { SocialNudgeToastProps } from './SocialNudgeToast.types';

/** Faces the stack draws. Beyond three the copy carries the count, not the row. */
const MAX_FACES = 3;

/**
 * The follow-nudge toast: a stack of faces, a line about them, and ONE button —
 * a row where there is room for one, a column on a phone.
 *
 * Two surfaces render it — the recipient of a shared Set ("@anna shared this
 * with you") and a study group ("you don't follow 4 people in this Group") —
 * and they must not drift, because they are the same offer made in two places.
 *
 * Three things it deliberately does NOT have:
 *   - **no ✕.** These toasts are persistent (the host's nudge mode), and the
 *     way out is the action or leaving the screen. A dismiss control on a
 *     persistent toast reads as "this is the last you'll see of it", which is
 *     not the product rule.
 *   - **no follow STATE.** The caller owns `actionState`; the block never
 *     mutates. That keeps the optimistic flip, the analytics capture and the
 *     error rollback in one place per platform.
 *   - **no auto-hide on `done`.** The settled button is the receipt.
 *
 * The lifecycle (queue, swipe, dismissal) belongs to each platform's toast
 * host, which renders this as a custom card — same contract as `Toast`.
 */
export function SocialNudgeToast({
  avatars,
  title,
  description,
  action,
  actionState = 'idle',
  onIdentityPress,
  doneLabel,
  className,
  testID,
}: SocialNudgeToastProps) {
  const faces = avatars.slice(0, MAX_FACES).map((a, i) => ({
    // Names repeat (two "Anna"s in one group is normal), so the index is the
    // only stable key here — the list is a fixed, non-reordering three.
    id: `${i}`,
    name: a.name,
    avatarUrl: a.src ?? null,
  }));
  const isDone = actionState === 'done';

  const button = (
    <Button
      variant={isDone ? 'secondary' : 'primary'}
      size="s"
      loading={actionState === 'busy'}
      leftIcon={isDone ? undefined : action.icon}
      testID={subTestID(testID, 'action')}
      // `done` is a receipt, not a toggle: the nudge offers the follow
      // once and never the unfollow. Dropping the handler (rather than
      // `disabled`, which fades the chip to 30%) keeps it legible.
      onPress={isDone ? undefined : action.onPress}>
      {isDone ? doneLabel : action.label}
    </Button>
  );

  return (
    <Toast
      variant="default"
      size="md"
      // Fill the width the host gives us and stop at the DS `md` ceiling,
      // instead of the size's own `w-fit min-w-[320px]`: a card that hugs its
      // copy sized itself off the SENTENCE, so on a phone it grew past the
      // screen (and the 320 floor kept it there) with the button hanging off
      // the right edge. `min-w-0` lets it shrink onto a 320pt device too.
      //
      // `auto`: the Figma ROW on a desktop toast column, and a vertical block —
      // faces, headline, line, button — on a narrow card (see
      // TOAST_STACKED_CLASS). This nudge's action is a sentence, not a word
      // ("Obserwuj znajomego"), which a 400pt card holds beside the copy and a
      // 320pt phone card does not: there it left the headline a couple of
      // characters wide, and wrapping the button below was not enough, because
      // the faces then sat alone beside that sliver. Which layout at which
      // width is the CARD's rule, so the two platforms cannot disagree on it.
      layout="auto"
      className={cn('w-full min-w-0 max-w-[560px]', className)}
      testID={testID}
      leadingSlot={
        faces.length > 0
          ? // `maxFaces` equals the slice, so the stack never renders a `+N`
            // bubble — the count this nudge is about lives in the copy, where it
            // can be a translated sentence rather than a number in a circle.
            // `tight`: this stack is a mark meaning "these people", not a roster
            // to read face by face (Figma 18:21856 — three 40px faces in 60px).
            wrapIdentity(
              <MemberAvatarStack
                members={faces}
                maxFaces={MAX_FACES}
                size="l"
                overlap="tight"
              />,
              onIdentityPress,
              title,
              subTestID(testID, 'identity-faces'),
            )
          : undefined
      }
      title={
        // A node, not a string, when the headline navigates: `Toast` wraps a
        // text-like title in its own `<Text>`, and a Pressable inside a Text
        // does not lay out on native. Passing the styled node keeps the DS
        // typography while the press target stays a real Pressable.
        onIdentityPress
          ? wrapIdentity(
              <Text className={toastTitleVariants({ variant: 'default', size: 'md' })}>
                {title}
              </Text>,
              onIdentityPress,
              title,
              subTestID(testID, 'identity-title'),
            )
          : title
      }
      description={description}
      actions={button}
    />
  );
}
SocialNudgeToast.displayName = 'SocialNudgeToast';

/** Make a slot open the person the nudge is about, when the caller wants that. */
function wrapIdentity(
  node: React.ReactNode,
  onPress: SocialNudgeToastProps['onIdentityPress'],
  accessibilityLabel: string,
  testID?: string,
): React.ReactNode {
  if (!onPress) return node;
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      onPress={onPress}>
      {node}
    </Pressable>
  );
}
